import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

export function useProducts() {
  const [state, setState] = useState({ products: null, error: false });
  useEffect(() => {
    let alive = true;
    fetch(`${API}/products`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => alive && setState({ products: d.products, error: false }))
      .catch(() => alive && setState({ products: [], error: true }));
    return () => { alive = false; };
  }, []);
  return state;
}

export const productHref = (p) => (p.status === "available" && p.cta_path?.startsWith("/") ? p.cta_path : `/applications/${p.slug}`);

export function ProductStatus({ p, testId }) {
  return p.status === "available" ? (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full bg-bx px-3 py-1 text-xs font-semibold text-white">
      <Sparkles className="h-3.5 w-3.5 text-gold" />Disponible{p.price_label ? ` · ${p.price_label}` : ""}
    </span>
  ) : (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-white px-3 py-1 text-xs font-semibold text-bx-2">
      <Clock3 className="h-3.5 w-3.5 text-gold" />Bientôt disponible
    </span>
  );
}

export const PRODUCT_LOGOS = { collaby: { icon: "/brand/collaby-icon.webp", full: "/brand/collaby-full.webp" } };

export function ProductCard({ p }) {
  const initials = p.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase();
  const logo = PRODUCT_LOGOS[p.slug];
  return (
    <Link to={productHref(p)} data-testid={`product-card-${p.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-[22px] border border-line bg-white p-7 shadow-[0_2px_10px_-4px_rgba(36,33,39,0.08)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-bx/30 hover:shadow-[0_22px_44px_-20px_rgba(101,31,56,0.35)]">
      <span className="absolute left-7 top-0 h-[3px] w-12 rounded-b-full bg-gold transition-[width,background-color] duration-500 group-hover:w-[calc(100%-3.5rem)] group-hover:bg-bx" aria-hidden />
      <div className="flex items-start justify-between gap-4">
        {logo ? <img src={logo.icon} alt={`Logo ${p.name}`} className="h-14 w-14 rounded-2xl object-cover ring-1 ring-gold/40" data-testid={`product-logo-${p.slug}`} />
          : <span className={`flex h-14 w-14 items-center justify-center rounded-2xl font-display text-lg font-semibold ${p.status === "available" ? "bg-bx text-white" : "bg-ivory text-bx ring-1 ring-gold/50"}`}>{initials}</span>}
        <ProductStatus p={p} testId={`product-status-${p.slug}`} />
      </div>
      <h3 className="mt-6 font-display text-2xl font-semibold text-ink group-hover:text-bx">{p.name}</h3>
      <p className="mt-2 flex-1 text-base leading-relaxed text-soft">{p.tagline}</p>
      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-bx">
        {p.status === "available" ? `Découvrir ${p.name}` : "Voir la présentation"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export function ProductGrid({ kind = "application", testId = "product-grid" }) {
  const { products, error } = useProducts();
  if (!products) return <div className="grid gap-5 md:grid-cols-2">{[0, 1].map((i) => <div key={i} className="h-64 animate-pulse rounded-[22px] bg-white/70" />)}</div>;
  if (error) return <p className="rounded-2xl border border-line bg-white p-6 text-soft" data-testid={`${testId}-error`}>Le catalogue n'a pas pu être chargé. Actualise la page dans un instant.</p>;
  const list = products.filter((p) => p.kind === kind);
  if (!list.length) return <p className="text-soft" data-testid={`${testId}-empty`}>Aucun produit pour le moment.</p>;
  return <div className="grid gap-5 md:grid-cols-2" data-testid={testId}>{list.map((p) => <ProductCard key={p.slug} p={p} />)}</div>;
}
